import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Ticket, Trophy, Calendar, ArrowLeft, Landmark } from "lucide-react";

export default function RaffleDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [raffle, setRaffle] = useState(null);
  const [qty, setQty] = useState(1);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/raffles/${id}`);
        setRaffle(r.data);
      } catch (e) {
        toast.error("Raffle not found");
      }
    })();
  }, [id]);

  const buy = async () => {
    if (!email || !name) {
      toast.error("Please enter your name and email");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/orders", {
        item_type: "raffle_ticket",
        item_id: id,
        quantity: qty,
        customer_email: email,
        customer_name: name,
      });
      nav(`/order/${res.data.order_reference}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally {
      setLoading(false);
    }
  };

  if (!raffle) {
    return <div className="max-w-7xl mx-auto px-6 py-20 font-mono-retro text-[#A8A39D] uppercase tracking-[0.2em]">Loading fixture…</div>;
  }

  const remaining = raffle.total_tickets - (raffle.tickets_sold || 0);
  const total = Number(raffle.ticket_price) * qty;

  return (
    <div data-testid="raffle-detail" className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <button onClick={() => nav(-1)} data-testid="back-btn" className="font-mono-retro text-xs tracking-[0.3em] text-[#A8A39D] uppercase inline-flex items-center gap-2 hover:text-[#C8A96A] mb-8">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="border border-[#332D2B] bg-[#1C1816]">
            <img src={raffle.image_url} alt={raffle.title} className="w-full aspect-[16/10] object-cover" />
            <div className="p-8">
              <div className="font-mono-retro text-[10px] tracking-[0.3em] text-[#756F6B] uppercase">Raffle No. {raffle.id.slice(-6)}</div>
              <h1 className="font-display text-5xl text-[#F2EBDD] mt-2 leading-tight">{raffle.title}</h1>
              <div className="flex flex-wrap gap-6 mt-5">
                <div className="flex items-center gap-2 text-[#A8A39D]"><Trophy size={16} className="text-[#C8A96A]" /> {raffle.prize}</div>
                <div className="flex items-center gap-2 text-[#A8A39D]"><Calendar size={16} className="text-[#C8A96A]" /> Draws {raffle.draw_date}</div>
              </div>
              <div className="perforated my-6" />
              <p className="text-[#F2EBDD]/90 leading-relaxed whitespace-pre-line">{raffle.description}</p>
            </div>
          </div>
        </div>

        <aside className="lg:col-span-5">
          <div className="border-2 border-[#332D2B] bg-[#1C1816] p-7 sticky top-28">
            <div className="font-mono-retro text-[10px] tracking-[0.3em] text-[#756F6B] uppercase">Match Ticket</div>
            <div className="flex items-end justify-between mt-2">
              <div className="font-display text-5xl text-[#C8A96A] leading-none">£{raffle.ticket_price}</div>
              <div className="font-mono-retro text-[10px] tracking-[0.25em] text-[#A8A39D] uppercase">per ticket</div>
            </div>
            <div className="perforated my-5" />
            <div className="grid grid-cols-2 gap-4 mb-5">
              <div>
                <div className="fz-label">Sold</div>
                <div className="font-display text-2xl text-[#F2EBDD]">{raffle.tickets_sold || 0}</div>
              </div>
              <div>
                <div className="fz-label">Remaining</div>
                <div className="font-display text-2xl text-[#F2EBDD]">{remaining}</div>
              </div>
            </div>

            <label className="fz-label">Tickets</label>
            <input
              data-testid="raffle-qty"
              type="number"
              min={1}
              max={remaining}
              value={qty}
              onChange={(e) => setQty(Math.max(1, Math.min(remaining, parseInt(e.target.value) || 1)))}
              className="fz-input"
            />

            <label className="fz-label mt-4">Full Name</label>
            <input
              data-testid="raffle-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="fz-input"
              placeholder="Your name"
            />

            <label className="fz-label mt-4">Email (for confirmation)</label>
            <input
              data-testid="raffle-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="fz-input"
              placeholder="you@matchday.com"
            />

            <div className="perforated my-6" />
            <div className="flex items-center justify-between mb-5">
              <span className="font-mono-retro text-[10px] tracking-[0.3em] text-[#A8A39D] uppercase">Total</span>
              <span className="font-display text-3xl text-[#C8A96A]">£{total.toFixed(2)}</span>
            </div>
            <button
              data-testid="raffle-buy-btn"
              onClick={buy}
              disabled={loading || remaining <= 0 || raffle.status !== "active"}
              className="btn-primary w-full justify-center"
            >
              <Ticket size={16} /> {loading ? "Placing order…" : "Buy Ticket"}
            </button>
            <div className="font-mono-retro text-[10px] tracking-[0.25em] text-[#756F6B] uppercase text-center mt-3 inline-flex items-center gap-2 w-full justify-center">
              <Landmark size={12} className="text-[#C8A96A]" /> UK Bank Transfer · Instant reference
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
